import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import "./AddCompanyPolicy.css";

import { apiCall } from "../api/apiCall";
import Navbar from "../components/Navbar/Navbar";
import TopHeader from "../components/Navbar/TopHeader";
import { Category, CategoryItemType } from "../models/category";
import { CategoryLabels } from "../utils/categoryLabels";
import generateToastContainer from "../utils/ToastContainer";
import { useTranslation } from "../i18n/UseTranslation";
import { CategorySelect } from "../components/CategorySelect";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function normalizeCategoryKey(itemType: string) {
  return itemType
    .toLowerCase()
    .replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

type PolicyFormData = {
  category_uuid: string;
  allowed: boolean;
  spending_limit: number | null;
  max_amount_without_receipt: number | null;
  requires_receipt: boolean;
  requires_supplier_name: boolean;
  requires_comment: boolean;
  allows_vat: boolean;
  allows_manual_vat: boolean;
  vat_rule_type: string;
  allows_distance_km: boolean;
  allows_fuel_expense: boolean;
  allow_company_car: boolean;
  allow_personal_car: boolean;
  is_active: boolean;
};

type ToggleItemProps = {
  label: string;
  value: boolean;
  onClick: () => void;
  disabled?: boolean;
};

function ToggleItem({
  label,
  value,
  onClick,
  disabled = false,
}: ToggleItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`policy-toggle-card ${value ? "active" : "inactive"}`}
      aria-pressed={value}
    >
      <span className={`policy-toggle-label ${value ? "active" : "inactive"}`}>
        {label}
      </span>

      <span
        className={`policy-toggle-switch ${value ? "active" : "inactive"}`}
        aria-hidden="true"
      >
        <span
          className={`policy-toggle-dot ${value ? "active" : "inactive"}`}
        />
      </span>
    </button>
  );
}

function AddCompanyPolicy() {
  const { companyUuid } = useParams<{
    companyUuid: string;
  }>();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const translations = t.companyPolicyForm;
  const categoryTypes = t.categoryTypes;

  const [formData, setFormData] = useState<PolicyFormData>({
    category_uuid: "",
    allowed: true,
    spending_limit: null,
    max_amount_without_receipt: null,
    requires_receipt: true,
    requires_supplier_name: false,
    requires_comment: false,
    allows_vat: true,
    allows_manual_vat: false,
    vat_rule_type: "SUM_LINES",
    allows_distance_km: false,
    allows_fuel_expense: false,
    allow_company_car: false,
    allow_personal_car: false,
    is_active: true,
  });

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const [isAuthReady, setIsAuthReady] = useState(false);

  const {
    data: categories = [],
    isLoading,
    isError,
  } = useQuery<Category[], Error>({
    queryKey: ["categories", companyUuid],
    queryFn: () =>
      apiCall<Category[]>(`/categories?company_uuid=${companyUuid}`),
    enabled: isAuthReady,
    refetchOnMount: true,
  });

  const categoryOptions = useMemo(
    () =>
      Object.keys(CategoryLabels)
        .map((key) => {
          const translationKey = normalizeCategoryKey(
            key,
          ) as keyof typeof categoryTypes;
          const category = categories.find((c) => c.item_type === key);

          return {
            uuid: category?.uuid ?? "",
            value: key as CategoryItemType,
            label:
              categoryTypes[translationKey] ??
              CategoryLabels[key as keyof typeof CategoryLabels] ??
              key,
          };
        })
        .filter((option) => option.uuid !== ""),
    [categoryTypes, categories],
  );

  const mutation = useMutation({
    mutationFn: (newData: PolicyFormData) =>
      apiCall(`/companies/${companyUuid}/policies`, {
        method: "POST",
        body: JSON.stringify(newData),
      }),

    onSuccess: () => {
      toast.success(translations.createSuccess);

      queryClient.invalidateQueries({
        queryKey: ["fetchCompanyPolicies", companyUuid],
      });

      navigate(-1);
    },

    onError: (error: Error) => {
      toast.error(translations.createError.replace("{message}", error.message));
    },
  });

  const handleToggle = (field: keyof PolicyFormData) => {
    setFormData((previous) => ({
      ...previous,
      [field]: !previous[field],
    }));
  };

  const handleChange = (
    field: keyof PolicyFormData,
    value: string | boolean,
  ) => {
    const numericField =
      field === "spending_limit" || field === "max_amount_without_receipt";

    const finalValue = numericField
      ? value === ""
        ? null
        : Number(value)
      : value;

    setFormData((previous) => ({
      ...previous,
      [field]: finalValue,
    }));
  };

  if (isLoading) {
    return (
      <div className="add-policy-page">
        <Navbar />
        <TopHeader />

        <main className="dashboard-wrapper">
          <div className="policy-loading">
            <div className="policy-loading-spinner" aria-hidden="true" />

            <p>{translations.loading}</p>
          </div>
        </main>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="add-policy-page">
        <Navbar />
        <TopHeader />

        <main className="dashboard-wrapper">
          <div className="policy-error">{translations.categoriesLoadError}</div>
        </main>

        {generateToastContainer()}
      </div>
    );
  }

  // Permet de retrouver le CategoryItemType actif à partir de l'UUID stocké dans formData
  const currentCategoryValue =
    (categoryOptions.find((c) => c.uuid === formData.category_uuid)
      ?.value as CategoryItemType) || ("" as CategoryItemType);

  return (
    <div className="add-policy-page">
      <Navbar />
      <TopHeader />

      <main className="dashboard-wrapper">
        <div className="add-policy-container">
          <div className="add-policy-card">
            <div className="add-policy-header">
              <h2>{translations.createTitle}</h2>

              <p>{translations.createSubtitle}</p>
            </div>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                mutation.mutate(formData);
              }}
            >
              <div className="add-policy-grid">
                <div className="policy-input-group">
                  <label htmlFor="policy-category">
                    {translations.fields.category}
                  </label>

                  <CategorySelect
                    className="policy-category"
                    value={currentCategoryValue}
                    onChange={(selectedValue) => {
                      if (!selectedValue) {
                        handleChange("category_uuid", "");
                        return;
                      }

                      const matchingCategory = categoryOptions.find(
                        (c) => c.value === selectedValue,
                      );

                      if (matchingCategory) {
                        handleChange("category_uuid", matchingCategory.uuid);
                      }
                    }}
                    options={[
                      {
                        value: "" as CategoryItemType,
                        label: translations.placeholders.chooseCategory,
                      },
                      ...categoryOptions.map((category) => ({
                        value: category.value,
                        label: category.label,
                      })),
                    ]}
                  />
                </div>

                <div className="policy-input-group">
                  <label htmlFor="spending-limit">
                    {translations.fields.spendingLimit}
                  </label>

                  <input
                    id="spending-limit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.spending_limit ?? ""}
                    onChange={(event) =>
                      handleChange("spending_limit", event.target.value)
                    }
                    placeholder={translations.placeholders.noLimit}
                  />
                </div>

                <div className="policy-input-group">
                  <label htmlFor="max-without-receipt">
                    {translations.fields.maxWithoutReceipt}
                  </label>

                  <input
                    id="max-without-receipt"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.max_amount_without_receipt ?? ""}
                    onChange={(event) =>
                      handleChange(
                        "max_amount_without_receipt",
                        event.target.value,
                      )
                    }
                    placeholder={translations.placeholders.zero}
                  />
                </div>

                <div className="policy-input-group">
                  <label htmlFor="vat-rule">
                    {translations.fields.vatRuleType}
                  </label>

                  <select
                    id="vat-rule"
                    value={formData.vat_rule_type}
                    onChange={(event) =>
                      handleChange("vat_rule_type", event.target.value)
                    }
                  >
                    <option value="NONE">{translations.vatRules.none}</option>

                    <option value="MANUAL">
                      {translations.vatRules.manual}
                    </option>

                    <option value="SUM_LINES">
                      {translations.vatRules.sumLines}
                    </option>
                  </select>
                </div>
              </div>

              <h3 className="policy-section-title">
                {translations.conditionsTitle}
              </h3>

              <div className="policy-toggles-container">
                <ToggleItem
                  label={translations.conditions.allowed}
                  value={formData.allowed}
                  onClick={() => handleToggle("allowed")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.requiresReceipt}
                  value={formData.requires_receipt}
                  onClick={() => handleToggle("requires_receipt")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowsVat}
                  value={formData.allows_vat}
                  onClick={() => handleToggle("allows_vat")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowsManualVat}
                  value={formData.allows_manual_vat}
                  onClick={() => handleToggle("allows_manual_vat")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.requiresComment}
                  value={formData.requires_comment}
                  onClick={() => handleToggle("requires_comment")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.requiresSupplierName}
                  value={formData.requires_supplier_name}
                  onClick={() => handleToggle("requires_supplier_name")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowsDistanceKm}
                  value={formData.allows_distance_km}
                  onClick={() => handleToggle("allows_distance_km")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowsFuelExpense}
                  value={formData.allows_fuel_expense}
                  onClick={() => handleToggle("allows_fuel_expense")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowCompanyCar}
                  value={formData.allow_company_car}
                  onClick={() => handleToggle("allow_company_car")}
                  disabled={mutation.isPending}
                />

                <ToggleItem
                  label={translations.conditions.allowPersonalCar}
                  value={formData.allow_personal_car}
                  onClick={() => handleToggle("allow_personal_car")}
                  disabled={mutation.isPending}
                />
              </div>

              <div className="add-policy-actions">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="policy-btn-cancel"
                  disabled={mutation.isPending}
                >
                  {translations.cancel}
                </button>

                <button
                  type="submit"
                  disabled={mutation.isPending || !formData.category_uuid}
                  className="policy-btn-submit"
                >
                  {mutation.isPending
                    ? translations.creating
                    : translations.createPolicy}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {generateToastContainer()}
    </div>
  );
}

export default AddCompanyPolicy;
